import { useEffect, useState } from 'react'
import FormPage from '../../../../Components/Form/Page/FormPage'
import useBriefContext from '../../../../Hooks/Brief/useBriefContext'
import getAdjacentSteps from '../../../../Logic/Form/Authorization/getAdjacentSteps'
import { LocationAttributes } from '../../../../Types/Location'
import BaseLocationInput from '../Base'
import InputView from '../../InputView'
import { SPACE_SEARCH_URL, VENUE_CHECKOUT_CITIES, VENUE_CHECKOUT_URL } from '../../../../Constants'
import { getDefaultLocationAttrs, LocationAttributesWithoutRadius } from '../Base/LocationInput'
import GeocodeErrorAlert from './GeocodeError'
import './LocationInput.less'
import './LocationInputAnimation.less'
import LoadingSpinner from '../../../../Components/LoadingSpinner'
import { getDefaultRadius } from '../../../../Utilities/Location'
import CombinedSlider from '../Base/CombinedSlider/CombinedSlider'

type SpaceSearchLocationAttrs = Omit<LocationAttributes, 'currency' | 'country' | 'leadFeedCity'>

type VenueCheckoutLocationAttrs = SpaceSearchLocationAttrs &
  Pick<LocationAttributes, 'currency' | 'country' | 'leadFeedCity'>

type RedirectTarget = 'space-search' | 'venue-checkout'

const MILES_TO_KM = 1.60934

const getSpaceSearchURL = (attrs: SpaceSearchLocationAttrs): URL => {
  const url = new URL(SPACE_SEARCH_URL)
  const searchEntries = Object.entries(attrs).map<[string, string]>(([prop, val]) => [
    `q[${prop}]`,
    `${val}`,
  ])
  const searchParams = new URLSearchParams([['utf8', '✓'], ...searchEntries])
    .toString()
    .replace(/%5B/g, '[')
    .replace(/%5D/g, ']')
    .replace(/%20/g, '+')

  return new URL(`${url.origin}${url.pathname}?${searchParams}`)
}

const getVenueCheckoutURL = ({
  lat,
  lon,
  radius,
  location,
  currency,
  country,
  leadFeedCity,
}: VenueCheckoutLocationAttrs): URL => {
  const url = new URL(VENUE_CHECKOUT_URL)
  const radiusKm = radius.unit === 'mi' ? radius.distance * MILES_TO_KM : radius.distance
  const searchParams = new URLSearchParams({
    source: 'brief-builder',
    lat: `${lat}`,
    lon: `${lon}`,
    radius: `${radiusKm}`,
    location,
    currency,
    country: country ?? '',
    leadFeedCity: leadFeedCity ?? '',
  }).toString()

  return new URL(`${url.origin}${url.pathname}?${searchParams}`)
}

const LocationInput: InputView = ({ path }) => {
  const { brief, dispatchBrief, loading, geocodeError } = useBriefContext()
  const [locationAttributes, setLocationAttributes] = useState<
    LocationAttributesWithoutRadius | undefined
  >(getDefaultLocationAttrs(brief))
  const [pageClassName, setPageClassName] = useState('')
  const [redirectTarget, setRedirectTarget] = useState<RedirectTarget | undefined>()
  const redirecting = redirectTarget !== undefined
  const [radius, setRadius] = useState<LocationAttributes['radius']>(
    brief.radius ?? getDefaultRadius(brief)
  )
  useEffect(() => {
    if (loading || locationAttributes === undefined) return

    setPageClassName('delay-map-animations')
  }, [loading])

  useEffect(() => {
    if (!redirectTarget) return

    const {
      lat,
      lon,
      radius,
      location: address,
      currency,
      country,
      leadFeedCity,
    } = allLocationAttrs
    if (lat !== undefined && lon !== undefined && radius !== undefined && address !== undefined) {
      let url: URL
      if (redirectTarget === 'venue-checkout') {
        if (currency === undefined || country === undefined) {
          throw new Error(
            `Could not redirect to venue-checkout, missing currency/country:\n${JSON.stringify(
              allLocationAttrs
            )}`
          )
        }
        url = getVenueCheckoutURL({
          lat,
          lon,
          radius,
          location: address,
          currency,
          country,
          leadFeedCity: leadFeedCity ?? null,
        })
      } else {
        url = getSpaceSearchURL({ lat, lon, radius, location: address })
      }
      window.location.assign(url)
      return () => setRedirectTarget(undefined)
    }
    throw new Error(
      `Could not redirect (${redirectTarget}) with location params:\n${JSON.stringify(
        allLocationAttrs
      )}`
    )
  }, [redirectTarget])

  const { isStepComplete } = getAdjacentSteps(path, brief)

  const allLocationAttrs = { radius, ...(locationAttributes && locationAttributes) }
  const leadFeedCity = locationAttributes?.leadFeedCity
  const shouldRedirectToVenueCheckout =
    typeof leadFeedCity === 'string' && VENUE_CHECKOUT_CITIES.includes(leadFeedCity)
  const isLeadFeedReady = leadFeedCity !== undefined && !shouldRedirectToVenueCheckout
  const isSpaceSearchReady = isStepComplete(allLocationAttrs)
  const onContinue = () => dispatchBrief({ updateAttributes: allLocationAttrs })
  const afterContinue = shouldRedirectToVenueCheckout
    ? () => setRedirectTarget('venue-checkout')
    : !isLeadFeedReady && isSpaceSearchReady
    ? () => setRedirectTarget('space-search')
    : undefined

  return (
    <FormPage
      path={path}
      metaTitle='Location - HeadBox'
      heading='What location works best?'
      alert={geocodeError && <GeocodeErrorAlert />}
      disableNav={redirecting}
      onContinue={onContinue}
      canContinue={isLeadFeedReady || isSpaceSearchReady}
      afterContinue={afterContinue}
      className={`location-input leadfeed ${pageClassName}`}>
      {redirecting ? (
        <LoadingSpinner
          fadeIn={true}
          accessibleText={
            redirectTarget === 'venue-checkout'
              ? 'Redirecting you to venue checkout...'
              : 'Redirecting you to venue search...'
          }
        />
      ) : (
        <>
          <BaseLocationInput
            radius={radius}
            locationAttributes={locationAttributes}
            setLocationAttributes={setLocationAttributes}
            setRadius={setRadius}>
            <CombinedSlider
              country={locationAttributes?.country}
              radius={radius}
              setRadius={setRadius}
              show={!!locationAttributes?.location}
              title={'You will mainly hear from venues inside the circle you choose:'}
            />
          </BaseLocationInput>
        </>
      )}
    </FormPage>
  )
}

export default LocationInput
